import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
export default function CSS_ColorKeywords() {
  const colorGroups = {
    "Basic Colors": [
      "black",
      "white",
      "red",
      "green",
      "blue",
      "yellow",
      "gray",
    ],
    "Extended Colors": [
      "tomato",
      "gold",
      "teal",
      "violet",
      "indigo",
      "coral",
      "aqua",
    ],
    "Dark Shades": [
      "navy",
      "maroon",
      "olive",
      "purple",
      "brown",
      "darkslategray",
    ],
    "Light Shades": [
      "lavender",
      "beige",
      "mintcream",
      "aliceblue",
      "lightpink",
      "honeydew",
    ],
    "Trendy Colors": [
      "dodgerblue",
      "hotpink",
      "mediumseagreen",
      "royalblue",
      "crimson",
      "orchid",
    ],
  };

  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">🎨 Advanced CSS: Color Keywords</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS supports over <b>140 named color keywords</b>, such as{" "}
        <code>tomato</code>, <code>dodgerblue</code>, and <code>hotpink</code>.
        These keywords are easy to use and provide a quick way to style elements
        without needing HEX or RGB codes.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            There are <b>140+ predefined color keywords</b> in CSS.
          </li>
          <li>
            Keywords are <b>case-insensitive</b> (<code>Red</code> ={" "}
            <code>red</code>).
          </li>
          <li>
            Some colors have <b>synonyms</b>, like <code>fuchsia</code> ={" "}
            <code>magenta</code>.
          </li>
          <li>
            Keywords improve readability but may be less precise than HEX or
            RGB.
          </li>
          <li>
            Useful for prototyping, quick UI styling, and teaching beginners.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Basic usage */
color: red;
background-color: lightblue;

/* Case insensitive */
color: Navy; /* same as navy */

/* Synonyms */
color: fuchsia; /* same as magenta */`}
        />
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Examples by Category</h2>
        {Object.entries(colorGroups).map(([group, colors]) => (
          <div key={group} style={{ marginBottom: "2rem" }}>
            <h3 className="section-subtitle">{group}</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {colors.map((c) => (
                <div
                  key={c}
                  style={{
                    background: c,
                    width: "150px",
                    height: "70px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: [
                      "yellow",
                      "white",
                      "beige",
                      "aliceblue",
                      "mintcream",
                      "honeydew",
                    ].includes(c)
                      ? "#000"
                      : "#fff",
                    fontWeight: "bold",
                    textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
                  }}
                >
                  {c}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Extended Palette */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Extended Palette</h2>
        <p>
          CSS keywords also include many <b>extended shades</b> such as{" "}
          <code>lightcoral</code>, <code>mediumslateblue</code>, and{" "}
          <code>darkseagreen</code>.
        </p>
        <CodeCopyBlock
          code={`p { color: lightcoral; }
h1 { color: mediumslateblue; }
div { background: darkseagreen; }`}
        />
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          While color keywords are easy to use, for **professional projects**
          it’s better to use <code>HEX</code>, <code>RGB</code>, or{" "}
          <code>HSL</code> for consistency and precision. Next, check out{" "}
          <a href="/css/colors-advanced" className="learn-more">
            CSS Colors Advanced
          </a>{" "}
          to explore modern formats like <code>HWB</code> and <code>LCH</code>.
        </p>
      </div>
    </div>
  );
}
